import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

// Types
interface TenantState {
  currentTenantId: string | null;
  tenantData: Record<string, any>;
  isolatedState: Record<string, any>;
  isLoading: boolean;
  error: string | null;
}

interface TenantData {
  subscriptionId: number;
  userId: number;
  planName: string;
  features: string[];
  settings: Record<string, any>;
}

// Initial state
const initialState: TenantState = {
  currentTenantId: null,
  tenantData: {},
  isolatedState: {},
  isLoading: false,
  error: null,
};

// Async thunks
export const switchTenant = createAsyncThunk(
  'tenant/switch',
  async (subscriptionId: number) => {
    // Charger les données du tenant
    const response = await fetch(`/api/subscriptions/${subscriptionId}/tenant-data/`);
    if (!response.ok) throw new Error('Failed to load tenant data');
    
    const tenantData = await response.json();
    return {
      tenantId: `sub_${subscriptionId}`,
      tenantData,
    };
  }
);

// Slice
export const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    // Setter state isolé par tenant
    setTenantState: (state, action: PayloadAction<{ key: string; value: any }>) => {
      const { key, value } = action.payload;
      if (state.currentTenantId) {
        if (!state.isolatedState[state.currentTenantId]) {
          state.isolatedState[state.currentTenantId] = {};
        }
        state.isolatedState[state.currentTenantId][key] = value;
      }
    },
    
    // Getter state isolé par tenant
    getTenantState: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (state.currentTenantId) {
        return state.isolatedState[state.currentTenantId]?.[key];
      }
      return null;
    },
    
    // Clear state tenant
    clearTenantState: (state) => {
      if (state.currentTenantId) {
        delete state.isolatedState[state.currentTenantId];
      }
    },
    
    // Reset complet
    resetTenant: (state) => {
      state.currentTenantId = null;
      state.tenantData = {};
      state.isolatedState = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(switchTenant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(switchTenant.fulfilled, (state, action) => {
        const { tenantId, tenantData } = action.payload;
        state.currentTenantId = tenantId;
        state.tenantData[tenantId] = tenantData;
        state.isLoading = false;
      })
      .addCase(switchTenant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Error switching tenant';
      });
  },
});

// Actions
export const { setTenantState, getTenantState, clearTenantState, resetTenant } = tenantSlice.actions;

// Selectors
export const selectCurrentTenantId = (state: RootState) => state.tenant.currentTenantId;
export const selectTenantData = (state: RootState, tenantId: string) => state.tenant.tenantData[tenantId];
export const selectTenantState = (state: RootState, key: string) => {
  const tenantId = state.tenant.currentTenantId;
  return tenantId ? state.tenant.isolatedState[tenantId]?.[key] : null;
};
export const selectCurrentTenantData = (state: RootState) => {
  const tenantId = state.tenant.currentTenantId;
  return tenantId ? state.tenant.tenantData[tenantId] : null;
};

export default tenantSlice.reducer;

